import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import urlPrefix from '@/helpers/url-prefix';
import { useUsers } from '@/hooks/useUsers';
import { reloadCards } from '@/store/ducks/cards';
import { showToast } from '@/store/ducks/toasts';

import UserDetailsComponent from './UserDetailsComponent';

const UserDetailsContainer = () => {
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false);
  const { id } = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { useUserDetails, useDeleteUserMutation } = useUsers();
  const { data: user, isLoading: isUserBusy } = useUserDetails(id);
  const deleteUserMutation = useDeleteUserMutation();

  const features = user?.features || [];
  const isFeaturesBusy = isUserBusy;

  const showDeleteConfirmation = useCallback(() => {
    setIsDeleteConfirmationVisible(true);
  }, []);

  const cancelDelete = useCallback(() => {
    setIsDeleteConfirmationVisible(false);
  }, []);

  const handleDelete = useCallback(() => {
    deleteUserMutation.mutate(id, {
      onSuccess: () => {
        navigate(urlPrefix('users'));
        dispatch(reloadCards());
        dispatch(
          showToast({
            type: 'success',
            title: intl.formatMessage({ id: 'SUCCESS' }),
            text: intl.formatMessage({ id: 'USER_DELETE_SUCCESS' }),
            duration: 5000,
          })
        );
      },
      onError: () => {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'USER_DELETE_ERROR' }),
            duration: 5000,
          })
        );
      },
    });
  }, [deleteUserMutation, id, dispatch, intl, navigate]);

  return (
    <UserDetailsComponent
      id={id}
      isFeaturesBusy={isFeaturesBusy}
      isUserBusy={isUserBusy}
      features={features}
      user={user}
      intl={intl}
      isDeleteConfirmationVisible={isDeleteConfirmationVisible}
      cancelDelete={cancelDelete}
      handleDelete={handleDelete}
      showDeleteConfirmation={showDeleteConfirmation}
    />
  );
};

export default UserDetailsContainer;
